/**
 * Voice utility functions for determining which voice to use based on message context
 */

// Voice resolution runs on every message render, so its debug logging floods the
// console in production. Gate it behind a flag (opt in via
// localStorage.debugVoice = '1' when diagnosing voice selection).
const DEBUG_VOICE = typeof window !== 'undefined' && window.localStorage?.getItem('debugVoice') === '1';
const vlog = (...args) => { if (DEBUG_VOICE) console.log(...args); };

/**
 * Get the appropriate voice ID for a message based on sender role and settings
 * @param {Object} message - The message object
 * @param {string} message.senderRole - The role of the message sender
 * @param {Object} userSettings - User-specific settings (may contain voice overrides)
 * @param {Object} platformSettings - Platform-wide voice settings
 * @param {Object} therapistSettings - Therapist-specific voice settings (optional)
 * @param {string} clientId - The client ID for therapist-specific client overrides (optional)
 * @returns {string|null} The voice ID to use, or null if no voice should be used
 */
export const getVoiceForMessage = (message, userSettings, platformSettings, therapistSettings = null, clientId = null) => {
  if (!message || !message.senderRole) {
    return null;
  }

  const voiceSettings = platformSettings?.voiceSettings || {};
  
  // Helper function to get therapist voice with hierarchy
  const getTherapistVoice = (voiceType, platformVoiceType) => {
    vlog('[voiceUtils] getTherapistVoice called with:', { 
      voiceType, 
      platformVoiceType, 
      clientId,
      hasTherapistSettings: !!therapistSettings,
      therapistSettingsKeys: therapistSettings ? Object.keys(therapistSettings) : [],
      therapistSettingsContent: therapistSettings
    });
    
    if (therapistSettings && clientId) {
      // Check for client-specific override first
      // MongoDB Maps are serialized to objects, so access as object property
      const clientOverride = therapistSettings.clientVoiceOverrides?.[clientId];
      vlog('Client override object:', clientOverride);
      
      if (clientOverride?.[voiceType]) {
        vlog(`Using client override for ${voiceType}:`, clientOverride[voiceType]);
        return clientOverride[voiceType];
      }
    }
    
    // Then check therapist defaults - these use 'default' prefix
    // Special handling for 'AI' to maintain uppercase
    let therapistDefaultKey;
    if (voiceType === 'aiTherapistVoice') {
      therapistDefaultKey = 'defaultAITherapistVoice';
    } else if (voiceType === 'humanTherapistVoice') {
      therapistDefaultKey = 'defaultHumanTherapistVoice';
    } else if (voiceType === 'clientVoice') {
      therapistDefaultKey = 'defaultClientVoice';
    } else {
      // Fallback for any other voice types
      therapistDefaultKey = `default${voiceType.charAt(0).toUpperCase()}${voiceType.slice(1)}`;
    }
    
    vlog('Looking for therapist default with key:', therapistDefaultKey);
    vlog('therapistSettings structure:', {
      hasVoiceSettings: !!therapistSettings?.voiceSettings,
      voiceSettingsKeys: therapistSettings?.voiceSettings ? Object.keys(therapistSettings.voiceSettings) : 'none',
      fullTherapistSettings: therapistSettings
    });
    
    if (therapistSettings?.voiceSettings?.[therapistDefaultKey]) {
      vlog(`Using therapist default ${therapistDefaultKey}:`, therapistSettings.voiceSettings[therapistDefaultKey]);
      return therapistSettings.voiceSettings[therapistDefaultKey];
    }
    
    // Legacy therapist voice for AI therapist
    if (voiceType === 'aiTherapistVoice' && therapistSettings?.elevenLabsVoiceId) {
      vlog('Using legacy therapist voice:', therapistSettings.elevenLabsVoiceId);
      return therapistSettings.elevenLabsVoiceId;
    }
    
    // Finally, check platform defaults
    if (platformVoiceType && voiceSettings[platformVoiceType]) {
      vlog('Using platform default:', voiceSettings[platformVoiceType]);
      return voiceSettings[platformVoiceType];
    }
    
    vlog('No voice found in hierarchy');
    return null;
  };
  
  switch (message.senderRole) {
    case 'ai_therapist':
    case 'ai': // Handle both 'ai' and 'ai_therapist' roles
      // Hierarchy: client-specific → therapist default → legacy therapist → platform default
      return getTherapistVoice('aiTherapistVoice', 'defaultAITherapistVoiceId') ||
             userSettings?.therapistInfo?.elevenLabsVoiceId || 
             voiceSettings.defaultAITherapistVoiceId ||
             platformSettings?.defaultElevenLabsVoiceId; // Legacy fallback
    
    case 'human_therapist':
    case 'therapist': // Also handle 'therapist' role for supervisor chat
      // Hierarchy: client-specific → therapist default → platform default
      return getTherapistVoice('humanTherapistVoice', 'defaultHumanTherapistVoiceId') ||
             userSettings?.therapistInfo?.humanVoiceId || 
             voiceSettings.defaultHumanTherapistVoiceId;
    
    case 'client':
      // Hierarchy: client-specific → therapist default → platform default
      return getTherapistVoice('clientVoice', 'defaultClientVoiceId') ||
             userSettings?.clientInfo?.preferredVoiceId || 
             voiceSettings.defaultClientVoiceId;
    
    case 'supervisor':
      // Check for therapist's supervisor voice override
      return userSettings?.therapistInfo?.supervisorVoiceId || 
             voiceSettings.defaultSupervisorVoiceId;
    
    case 'system':
      // System messages always use platform default
      return voiceSettings.defaultSystemVoiceId;
    
    default:
      // Unknown sender role - no voice
      console.warn('Unknown sender role for voice selection:', message.senderRole);
      return null;
  }
};

/**
 * Get voice settings for the current user context
 * @param {string} userRole - The current user's role ('client', 'therapist', 'admin')
 * @param {Object} userSettings - User-specific settings
 * @param {Object} platformSettings - Platform-wide settings
 * @returns {Object} Object containing relevant voice IDs for the user
 */
export const getUserVoiceSettings = (userRole, userSettings, platformSettings) => {
  const voiceSettings = platformSettings?.voiceSettings || {};
  
  if (userRole === 'therapist') {
    return {
      aiTherapistVoice: userSettings?.therapistInfo?.elevenLabsVoiceId || 
                        voiceSettings.defaultAITherapistVoiceId,
      humanVoice: userSettings?.therapistInfo?.humanVoiceId || 
                  voiceSettings.defaultHumanTherapistVoiceId,
      supervisorVoice: userSettings?.therapistInfo?.supervisorVoiceId || 
                       voiceSettings.defaultSupervisorVoiceId,
      systemVoice: voiceSettings.defaultSystemVoiceId
    };
  } else if (userRole === 'client') {
    return {
      clientVoice: userSettings?.clientInfo?.preferredVoiceId || 
                   voiceSettings.defaultClientVoiceId,
      aiTherapistVoice: userSettings?.assignedTherapist?.therapistInfo?.elevenLabsVoiceId || 
                        voiceSettings.defaultAITherapistVoiceId,
      systemVoice: voiceSettings.defaultSystemVoiceId
    };
  } else {
    // Admin or other roles - return all defaults
    return voiceSettings;
  }
};

/**
 * Check if text-to-speech should be enabled for a message
 * @param {Object} message - The message object
 * @param {Object} userPreferences - User's TTS preferences
 * @returns {boolean} Whether TTS should be enabled
 */
export const shouldEnableTTS = (message, userPreferences) => {
  // Check if user has disabled TTS globally
  if (userPreferences?.disableTTS === true) {
    return false;
  }
  
  // Check role-specific preferences
  if (userPreferences?.ttsRoles) {
    return userPreferences.ttsRoles[message.senderRole] !== false;
  }
  
  // Default: enable TTS for all roles
  return true;
};

/**
 * Get the appropriate voice ID for speech-to-text transcription playback
 * @param {string} userRole - The role of the user who is speaking
 * @param {Object} userSettings - User-specific settings
 * @param {Object} platformSettings - Platform-wide settings
 * @returns {string|null} The voice ID to use for playback
 */
export const getVoiceForTranscript = (userRole, userSettings, platformSettings) => {
  const voiceSettings = platformSettings?.voiceSettings || {};
  
  switch (userRole) {
    case 'client':
      return userSettings?.clientInfo?.preferredVoiceId || 
             voiceSettings.defaultClientVoiceId;
    
    case 'therapist':
      return userSettings?.therapistInfo?.humanVoiceId || 
             voiceSettings.defaultHumanTherapistVoiceId;
    
    case 'supervisor':
      return userSettings?.therapistInfo?.supervisorVoiceId || 
             voiceSettings.defaultSupervisorVoiceId;
    
    default:
      return null;
  }
};